import React from 'react';
import { cn } from 'lib/utils';

/** Always reads as a *state* (dot + word), unlike the generic `Badge`. Callers map their status → a `tone`. */
export type StatusTone = 'neutral' | 'success' | 'warning' | 'error' | 'info' | 'done';

export interface StatusBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone?: StatusTone;
  /** Show the leading status dot. */
  dot?: boolean;
  size?: 'sm' | 'md';
}

const toneClasses: Record<StatusTone, { pill: string; dot: string }> = {
  neutral: {
    pill: 'bg-muted text-foreground/70',
    dot: 'bg-muted-foreground/60',
  },
  success: {
    pill: 'bg-success-50 text-success-700 dark:bg-success-900/30 dark:text-success-400',
    dot: 'bg-success-600 dark:bg-success-500',
  },
  warning: {
    pill: 'bg-warning-50 text-warning-700 dark:bg-warning-900/30 dark:text-warning-400',
    dot: 'bg-warning-500',
  },
  error: {
    pill: 'bg-error-50 text-error-700 dark:bg-error-900/30 dark:text-error-400',
    dot: 'bg-error-600 dark:bg-error-500',
  },
  info: {
    pill: 'bg-info-50 text-info-700 dark:bg-info-900/30 dark:text-info-400',
    dot: 'bg-info-500',
  },
  // Finished work is neither a success nor a failure: neutral tint, ink dot.
  done: {
    pill: 'bg-muted text-foreground',
    dot: 'bg-foreground',
  },
};

const sizeClasses = {
  sm: 'px-2 py-0.5 text-xs gap-1.5',
  md: 'px-2.5 py-1 text-sm gap-2',
};

const StatusBadge = React.forwardRef<HTMLSpanElement, StatusBadgeProps>(
  ({ tone = 'neutral', dot = true, size = 'sm', className, children, ...props }, ref) => {
    const tones = toneClasses[tone];
    return (
      <span
        ref={ref}
        className={cn(
          // Same shape as Badge — a label is a label. The leading dot, not a
          // pill outline, is what marks this one as carrying state.
          'inline-flex items-center rounded-md font-medium whitespace-nowrap',
          tones.pill,
          sizeClasses[size],
          className,
        )}
        {...props}
      >
        {dot && <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', tones.dot)} aria-hidden />}
        {children}
      </span>
    );
  },
);

StatusBadge.displayName = 'StatusBadge';

/** ≥78 success · 60–77 warning · <60 error, so score bands stay consistent app-wide. */
export function scoreTone(percent: number): Extract<StatusTone, 'success' | 'warning' | 'error'> {
  if (percent >= 78) return 'success';
  if (percent >= 60) return 'warning';
  return 'error';
}

const SCORE_TEXT: Record<ReturnType<typeof scoreTone>, string> = {
  success: 'text-success-700 dark:text-success-400',
  warning: 'text-warning-700 dark:text-warning-400',
  error: 'text-error-700 dark:text-error-400',
};

/** Text colour for a percentage score shown on its own (no pill), in the same bands. */
export function scoreTextClass(percent: number): string {
  return SCORE_TEXT[scoreTone(percent)];
}

export { StatusBadge };
export default StatusBadge;
